import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { AuthState } from '../store/auth/auth.state';
import { PermissionUtils, ModuleType } from '../utils/permission.utils';

export interface ModuleAccessState {
  accessibleModules: ModuleType[];
  canAccessMultiple: boolean;
  singleModuleAccess: ModuleType | null;
  currentModule: ModuleType | null;
}

@Injectable({
  providedIn: 'root'
})
export class ModuleAccessService {
  private _state$ = new BehaviorSubject<ModuleAccessState>({
    accessibleModules: [],
    canAccessMultiple: false,
    singleModuleAccess: null,
    currentModule: null,
  });

  private _lastVisitedUrls = new Map<ModuleType, string>();

  constructor(
    private store: Store,
    private permissionUtils: PermissionUtils,
    private router: Router
  ) {
    this.initializeModuleAccess();
    this.trackNavigationUrls();
    this.subscribeToPermissionChanges();
  }

  /**
   * Re-inicializa el acceso a módulos cuando cambian los permisos del usuario
   * (por ejemplo, al editar su rol o al hacer GetMe después de un cambio)
   */
  private subscribeToPermissionChanges(): void {
    this.store.select(AuthState.getPermissions).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ).subscribe(() => {
      this.initializeModuleAccess();
    });
  }

  /**
   * Escucha NavigationEnd y guarda la URL completa (con query params)
   * asociada al módulo actual
   */
  private trackNavigationUrls(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(event => {
      const currentModule = this._state$.getValue().currentModule;
      const url = event.urlAfterRedirects;
      if (currentModule && !url.startsWith('/modulos') && !url.startsWith('/admin/access-denied')) {
        this._lastVisitedUrls.set(currentModule, url);
      }
    });
  }

  /**
   * Inicializa el acceso a módulos basado en los permisos del usuario
   */
  private initializeModuleAccess(): void {
    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);
    const authUser = this.store.selectSnapshot(AuthState.getUser);

    if ((!userPermissions || userPermissions.length === 0) && !authUser?.isSuperAdmin) {
      // Permisos vacíos = logout. Resetear todo para evitar estado stale entre sesiones.
      this._state$.next({
        accessibleModules: [],
        canAccessMultiple: false,
        singleModuleAccess: null,
        currentModule: null,
      });
      this._lastVisitedUrls.clear();
      return;
    }

    const accessibleModules = this.permissionUtils.getAccessibleModules();
    const canAccessMultiple = this.permissionUtils.canAccessMultipleModules();
    const singleModuleAccess = this.permissionUtils.getSingleAccessibleModule();

    const currentState = this._state$.getValue();
    
    // Limpiar módulo actual si ya no tiene acceso
    const currentModule = currentState.currentModule && accessibleModules.includes(currentState.currentModule)
      ? currentState.currentModule
      : null;
    
    this._state$.next({
      ...currentState,
      accessibleModules,
      canAccessMultiple,
      singleModuleAccess,
      currentModule,
    });

    console.log('🔐 ModuleAccessService - Módulos accesibles:', {
      accessibleModules,
      canAccessMultiple,
      singleModuleAccess,
      userPermissions: userPermissions.length
    });
  }

  /**
   * Refresca el estado de acceso a módulos (útil después de cambios en permisos)
   */
  refreshModuleAccess(): void {
    this.initializeModuleAccess();
  }

  /**
   * Alias para refreshModuleAccess (usado en tests)
   */
  refreshModuleDetection(): void {
    this.refreshModuleAccess();
  }

  /**
   * Determina a qué módulo debe redirigirse el usuario
   */
  getRedirectModule(): ModuleType | null {
    const state = this._state$.getValue();
    return state.singleModuleAccess || state.currentModule || state.accessibleModules[0] || null;
  }

  /**
   * Obtiene los módulos a los que el usuario tiene acceso
   */
  getAccessibleModules(): ModuleType[] {
    return this._state$.getValue().accessibleModules;
  }

  /**
   * Verifica si el usuario puede acceder a un módulo específico
   */
  canAccessModule(module: ModuleType): boolean {
    return this._state$.getValue().accessibleModules.includes(module);
  }

  /**
   * Observable del estado actual
   */
  get state$(): Observable<ModuleAccessState> {
    return this._state$.asObservable();
  }

  /**
   * Snapshot del estado actual
   */
  getCurrentState(): ModuleAccessState {
    return this._state$.getValue();
  }

  /**
   * Observable del módulo actual
   */
  get currentModule$(): Observable<ModuleType | null> {
    return this._state$.asObservable().pipe(
      map(state => state.currentModule),
      distinctUntilChanged()
    );
  }

  /**
   * Observable de los módulos accesibles
   */
  get accessibleModules$(): Observable<ModuleType[]> {
    return this._state$.asObservable().pipe(
      map(state => state.accessibleModules),
      distinctUntilChanged()
    );
  }

  /**
   * Observable de acceso múltiple
   */
  get canAccessMultiple$(): Observable<boolean> {
    return this._state$.asObservable().pipe(
      map(state => state.canAccessMultiple),
      distinctUntilChanged()
    );
  }

  /**
   * Observable de acceso a módulo único
   */
  get singleModuleAccess$(): Observable<ModuleType | null> {
    return this._state$.asObservable().pipe(
      map(state => state.singleModuleAccess),
      distinctUntilChanged()
    );
  }

  /**
   * Observable de información de módulos accesibles
   */
  get accessibleModulesInfo$(): Observable<any[]> {
    return this._state$.asObservable().pipe(
      map(state => state.accessibleModules.map(module => 
        this.permissionUtils.getModuleInfo(module)
      )),
      distinctUntilChanged()
    );
  }

  /**
   * Establece el módulo actual
   */
  setCurrentModule(module: ModuleType): void {
    const currentState = this._state$.getValue();

    // Validar directamente desde el store para evitar fallos silenciosos
    // cuando accessibleModules todavía no se ha poblado (timing en init)
    if (!this.permissionUtils.canAccessModule(module)) {
      console.warn(`No se puede establecer el módulo ${module}: no tiene acceso`);
      return;
    }
    
    this._state$.next({
      ...currentState,
      currentModule: module
    });

    console.log('📍 ModuleAccessService - Módulo actual establecido:', module);
  }

  /**
   * Limpia el módulo actual
   */
  clearCurrentModule(): void {
    const currentState = this._state$.getValue();
    
    this._state$.next({
      ...currentState,
      currentModule: null
    });
  }

  /**
   * Determina si el usuario necesita ver el hub de módulos
   */
  needsModuleHub(): boolean {
    const state = this._state$.getValue();
    return state.canAccessMultiple;
  }

  /**
   * Obtiene estadísticas de acceso a módulos
   */
  getAccessStats(): any {
    const state = this._state$.getValue();
    const allModules = Object.values(ModuleType);
    
    return {
      totalModules: allModules.length,
      accessibleModules: state.accessibleModules.length,
      hasMultipleAccess: state.canAccessMultiple,
      currentModule: state.currentModule,
      moduleNames: state.accessibleModules.map(module => 
        this.permissionUtils.getModuleInfo(module).name
      )
    };
  }

  /**
   * Guarda manualmente la última URL visitada para un módulo
   */
  saveModuleUrl(module: ModuleType, url: string): void {
    this._lastVisitedUrls.set(module, url);
  }

  /**
   * Obtiene la última URL visitada de un módulo
   */
  getLastModuleUrl(module: ModuleType): string | null {
    return this._lastVisitedUrls.get(module) ?? null;
  }

  /**
   * Obtiene todas las URLs guardadas (útil para debugging)
   */
  getAllLastVisitedUrls(): Record<string, string> {
    const result: Record<string, string> = {};
    this._lastVisitedUrls.forEach((url, module) => {
      result[module] = url;
    });
    return result;
  }

  /**
   * Limpia todas las URLs guardadas (útil al hacer logout)
   */
  clearLastVisitedUrls(): void {
    this._lastVisitedUrls.clear();
  }

  /**
   * Obtiene información completa del estado para debugging
   */
  getDebugInfo(): any {
    const state = this._state$.getValue();
    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);
    
    return {
      state,
      userPermissions,
      lastVisitedUrls: this.getAllLastVisitedUrls(),
      moduleInfo: state.accessibleModules.reduce((acc, module) => {
        acc[module] = this.permissionUtils.getModuleInfo(module);
        return acc;
      }, {} as Record<string, any>)
    };
  }

}
